import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosInstance from "../utils/axiosInstance";

export const uploadNewDonorsFile = createAsyncThunk(
  "importData/uploadNewDonorsFile",
  async (file, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const formData = new FormData();
      formData.append("file", file);
      const response = await axiosInstance.post(
        "api/donors/import-excel",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${auth.token}`,
          },
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error uploading file:", error);
      return rejectWithValue(error.response?.data || "Failed to upload file");
    }
  },
);

export const uploadExistingDonorsFile = createAsyncThunk(
  "importData/uploadExistingDonorsFile",
  async (file, { getState, dispatch, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const formData = new FormData();
      formData.append("file", file);
      const response = await axiosInstance.post(
        "api/import-data/import-existing-donors",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${auth.token}`,
          },
        },
      );

      // Clear cache and silently refetch donors after successful upload
      const { clearCacheDB } = await import("../utils/indexedDBCache");
      await clearCacheDB("all_donors");
      const { fetchDonors } = await import("./donorSlice");
      dispatch(fetchDonors({ _silent: true }));

      return response.data;
    } catch (error) {
      console.error("Error uploading file:", error);
      return rejectWithValue(error.response?.data || "Failed to upload file");
    }
  },
);

export const uploadPrasadFile = createAsyncThunk(
  "importData/uploadPrasadFile",
  async (file, { getState, dispatch, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const formData = new FormData();
      formData.append("file", file);
      const response = await axiosInstance.post(
        "api/import-data/set-prasad-yes",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${auth.token}`,
          },
        },
      );

      // Clear cache and silently refetch donors after successful upload
      const { clearCacheDB } = await import("../utils/indexedDBCache");
      await clearCacheDB("all_donors");
      const { fetchDonors } = await import("./donorSlice");
      dispatch(fetchDonors({ _silent: true }));
      return response.data;
    } catch (error) {
      console.error("Error uploading file:", error);
      return rejectWithValue(error.response?.data || "Failed to upload file");
    }
  },
);

export const uploadStatusFile = createAsyncThunk(
  "importData/uploadStatusFile",
  async (file, { getState, dispatch, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const formData = new FormData();
      formData.append("file", file);
      const response = await axiosInstance.post(
        "api/import-data/update-status",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${auth.token}`,
          },
        },
      );

      // Clear cache and silently refetch donors after successful upload
      const { clearCacheDB } = await import("../utils/indexedDBCache");
      await clearCacheDB("all_donors");
      const { fetchDonors } = await import("./donorSlice");
      dispatch(fetchDonors({ _silent: true }));

      return response.data;
    } catch (error) {
      console.error("Error uploading file:", error);
      return rejectWithValue(error.response?.data || "Failed to upload file");
    }
  },
);

export const uploadLocationFile = createAsyncThunk(
  "importData/uploadLocationFile",
  async (file, { getState, dispatch, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const formData = new FormData();
      formData.append("file", file);
      const response = await axiosInstance.post(
        "api/import-data/update-location",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${auth.token}`,
          },
        },
      );

      // Clear cache and silently refetch donors after successful upload
      const { clearCacheDB } = await import("../utils/indexedDBCache");
      await clearCacheDB("all_donors");
      const { fetchDonors } = await import("./donorSlice");
      dispatch(fetchDonors({ _silent: true }));

      return response.data;
    } catch (error) {
      console.error("Error uploading file:", error);
      return rejectWithValue(error.response?.data || "Failed to upload file");
    }
  },
);

const importDataSlice = createSlice({
  name: "importData",
  initialState: {
    loading: false,
    success: null,
    message: "",
    error: null,
    existingErrorDetails: {},
    prasadMissingDonors: [],
    prasadIncompleteDonors: [],
  },
  reducers: {
    clearImportState(state) {
      state.loading = false;
      state.success = null;
      state.message = "";
      state.error = null;
      state.existingErrorDetails = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(uploadExistingDonorsFile.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
        state.message = "";
      })
      .addCase(uploadExistingDonorsFile.fulfilled, (state, action) => {
        state.loading = false;
        state.success = action.payload.success;
        state.message = action.payload.message;
        state.existingErrorDetails = action.payload.errors || {};
      })
      .addCase(uploadExistingDonorsFile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to upload file";
      })
      .addCase(uploadPrasadFile.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
        state.message = "";
      })
      .addCase(uploadPrasadFile.fulfilled, (state, action) => {
        state.loading = false;
        state.success = action.payload.success;
        state.message = action.payload.message;
        state.prasadMissingDonors = action.payload.missingDonors || [];
        state.prasadIncompleteDonors = action.payload.incompleteDonors || [];
      })
      .addCase(uploadPrasadFile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to upload file";
      })
      .addCase(uploadStatusFile.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
        state.message = "";
      })
      .addCase(uploadStatusFile.fulfilled, (state, action) => {
        state.loading = false;
        state.success = action.payload.success;
        state.message = action.payload.message;
      })
      .addCase(uploadStatusFile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to upload file";
      })
      .addCase(uploadLocationFile.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
        state.message = "";
      })
      .addCase(uploadLocationFile.fulfilled, (state, action) => {
        state.loading = false;
        state.success = action.payload.success;
        state.message = action.payload.message;
      })
      .addCase(uploadLocationFile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to upload file";
      });
  },
});

export const { clearImportState } = importDataSlice.actions;
export default importDataSlice.reducer;
